import React from 'react'

import { CONTAINER_TYPES } from './constants'
import { StyledTreeItem } from './styles'
import { TreeItemLabel } from './TreeItemLabel'
import { DragDropHandlers, DropPosition, StructureTreeItem, TreeV2Props } from './types'

export interface TreeNodeProps {
	actionComponent?: TreeV2Props['actionComponent'],
	checkedItemIds: number[],
	childrenMap: Record<string, StructureTreeItem[]>,
	depth?: number,
	dropTarget: { id: string; position: DropPosition } | null,
	expandedItems: string[],
	getDragDropHandlers?: (item: StructureTreeItem) => DragDropHandlers | null,
	iconComponent?: TreeV2Props['iconComponent'],
	isCheckboxDisabled?: boolean,
	isIndeterminate: (itemId: string) => boolean,
	item: StructureTreeItem,
	loadingIds: Set<string>,
	onCheckChange: (itemId: number, checked: boolean, itemType: string, isCurrentlyIndeterminate: boolean) => void,
	onContextMenu: (item: StructureTreeItem, e: React.MouseEvent) => void,
	onRowClick: (item: StructureTreeItem, isExpanded: boolean, e: React.MouseEvent) => void,
	onRowDoubleClick: (item: StructureTreeItem, isExpanded: boolean, isContainer: boolean, e: React.MouseEvent) => void,
	/** Set of item IDs that have children (computed from childrenMap) */
	parentIdsWithChildren: Set<string>,
	pendingRefreshParentIds?: Set<string>,
	selectMode: boolean,
	setDropTarget: (target: { id: string; position: DropPosition } | null) => void
}

export const TreeNode = ({
	item,
	depth = 0,
	expandedItems,
	loadingIds,
	pendingRefreshParentIds,
	childrenMap,
	parentIdsWithChildren,
	iconComponent,
	actionComponent,
	selectMode,
	checkedItemIds,
	isCheckboxDisabled,
	dropTarget,
	getDragDropHandlers,
	onRowClick,
	onRowDoubleClick,
	onContextMenu,
	onCheckChange,
	setDropTarget,
	isIndeterminate,
}: TreeNodeProps): React.ReactNode => {
	if (depth > 20) return null

	const isExpanded = expandedItems.includes(item.id)
	const isLoading = loadingIds.has(item.id)
	const isContainer = CONTAINER_TYPES.includes(item.type)
	const children = childrenMap[item.id]
	const dragHandlers = getDragDropHandlers?.(item)
	// If this item's parent is pending refresh OR currently loading, hide incorrect arrows
	const parentId = item.parentStructureId ? String(item.parentStructureId) : undefined
	const isParentPendingRefresh = parentId && pendingRefreshParentIds?.has(parentId)
	const isParentLoading = parentId && loadingIds.has(parentId)

	const getDropPosition = (e: React.DragEvent): DropPosition => {
		const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
		const y = e.clientY - rect.top
		const height = rect.height

		if (dragHandlers?.canDropInside) {
			if (y < height * 0.25) return 'before'
			if (y > height * 0.75) return 'after'
			return 'inside'
		} else {
			return y < height * 0.5 ? 'before' : 'after'
		}
	}

	const handleDragOver = (e: React.DragEvent) => {
		if (!dragHandlers) return
		e.preventDefault()
		e.stopPropagation()
		const position = getDropPosition(e)
		setDropTarget({ id: item.id, position })
		dragHandlers.onDragOver(e)
	}

	const handleDrop = (e: React.DragEvent) => {
		if (!dragHandlers) return
		e.preventDefault()
		e.stopPropagation()
		const position = getDropPosition(e)
		setDropTarget(null)
		dragHandlers.onDrop(e, position)
	}

	const handleDragLeave = (e: React.DragEvent) => {
		if (!dragHandlers) return
		setDropTarget(null)
		dragHandlers.onDragLeave(e)
	}

	const isDropTarget = dropTarget?.id === item.id
	const dropIndicatorStyle = isDropTarget
		? {
				position: 'relative' as const,
				'&::before':
					dropTarget.position === 'before'
						? {
								content: '""',
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								height: '2px',
								backgroundColor: 'primary.main',
							}
						: undefined,
				'&::after':
					dropTarget.position === 'after'
						? {
								content: '""',
								position: 'absolute',
								bottom: 0,
								left: 0,
								right: 0,
								height: '2px',
								backgroundColor: 'primary.main',
							}
						: undefined,
				backgroundColor:
					dropTarget.position === 'inside' ? 'action.selected' : undefined,
			}
		: {}

	return (
		<StyledTreeItem
			key={item.id}
			itemId={item.id}
			data-id={item.id}
			data-testid={`tree-node-${item.type}-${item.label}`}
			onClick={(e) => onRowClick(item, isExpanded, e)}
			onDoubleClick={(e) => onRowDoubleClick(item, isExpanded, isContainer, e)}
			draggable={dragHandlers?.draggable}
			onDragStart={dragHandlers?.onDragStart}
			onDragOver={handleDragOver}
			onDragEnter={dragHandlers?.onDragEnter}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
			label={
				<TreeItemLabel
					item={item}
					iconComponent={iconComponent}
					actionComponent={actionComponent}
					isExpanded={isExpanded}
					isLoading={isLoading}
					selectMode={selectMode}
					isChecked={checkedItemIds.includes(item.structureId)}
					isIndeterminate={isIndeterminate(item.id)}
					isCheckboxDisabled={isCheckboxDisabled}
					onCheckChange={(checked) => onCheckChange(item.structureId, checked, item.type, isIndeterminate(item.id))}
				/>
			}
			onContextMenu={(e) => {
				e.preventDefault()
				e.stopPropagation()
				onContextMenu(item, e)
			}}
			sx={dropIndicatorStyle}
		>
		{children && children.length > 0
			? children.map((child) => (
					<TreeNode
						key={child.id}
						item={child}
						depth={depth + 1}
						expandedItems={expandedItems}
						loadingIds={loadingIds}
						pendingRefreshParentIds={pendingRefreshParentIds}
						childrenMap={childrenMap}
						parentIdsWithChildren={parentIdsWithChildren}
						iconComponent={iconComponent}
						actionComponent={actionComponent}
						selectMode={selectMode}
						checkedItemIds={checkedItemIds}
						isCheckboxDisabled={isCheckboxDisabled}
						dropTarget={dropTarget}
						getDragDropHandlers={getDragDropHandlers}
						onRowClick={onRowClick}
						onRowDoubleClick={onRowDoubleClick}
						onContextMenu={onContextMenu}
						onCheckChange={onCheckChange}
						setDropTarget={setDropTarget}
						isIndeterminate={isIndeterminate}
					/>
				))
			: isContainer && children === undefined && parentIdsWithChildren.has(item.id) && !isParentPendingRefresh && !isParentLoading
				? <StyledTreeItem itemId={`${item.id}-placeholder`} sx={{ display: 'none' }} label="" />
				: null}
		</StyledTreeItem>
	)
}

